import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, catchError, debounceTime, distinctUntilChanged, finalize, of, switchMap } from 'rxjs';
import { SellerBusiness } from '../../core/models/product.models';
import { BusinessService, CreateInternalBusinessRequest } from '../../core/services/business.service';
import { httpErrorMessage } from '../../core/utils/http-error-message';
import { LocationService, LocationSuggestion } from '../../core/services/location.service';

type BusinessQueue = 'Requested' | 'Approved' | 'CorrectionRequested' | 'OnHold' | 'Rejected' | 'All';

@Component({ selector: 'app-businesses', imports: [RouterLink, FormsModule], templateUrl: './businesses.html', styleUrl: './businesses.scss' })
export class Businesses implements OnInit {
  private readonly businessesService = inject(BusinessService);
  private readonly locations = inject(LocationService);
  readonly businesses = signal<SellerBusiness[]>([]);
  readonly isLoading = signal(true);
  readonly errorMessage = signal('');
  readonly selectedQueue = signal<BusinessQueue>('Requested');
  readonly showInternalForm = signal(false);
  readonly isCreating = signal(false);
  readonly createMessage = signal('');
  readonly createError = signal('');
  readonly addressSuggestions = signal<LocationSuggestion[]>([]);
  readonly isAddressSearching = signal(false);
  readonly selectedAddress = signal('');
  private readonly addressTerms = new Subject<string>();
  internalForm: CreateInternalBusinessRequest = this.emptyInternalForm();
  readonly counts = computed(() => {
    const items = this.businesses();
    const count = (status: string) => items.filter((item) => (item.approvalStatus || 'Pending') === status).length;
    return { requested: count('Pending'), approved: count('Approved'), corrections: count('CorrectionRequested'), onHold: count('OnHold'), rejected: count('Rejected'), all: items.length };
  });
  readonly visibleBusinesses = computed(() => {
    const queue = this.selectedQueue();
    if (queue === 'All') return this.businesses();
    const status = queue === 'Requested' ? 'Pending' : queue;
    return this.businesses().filter((item) => (item.approvalStatus || 'Pending') === status);
  });
  readonly internalBusiness = computed(() => this.businesses().find((item) => item.businessKind === 'PlatformOwned') ?? null);

  ngOnInit(): void {
    this.addressTerms.pipe(
      debounceTime(300), distinctUntilChanged(),
      switchMap((input) => {
        this.isAddressSearching.set(true); this.createError.set('');
        return this.locations.autocomplete(input).pipe(
          catchError((error) => { this.createError.set(httpErrorMessage(error, 'Unable to search Google addresses.')); return of([]); }),
          finalize(() => this.isAddressSearching.set(false)),
        );
      }),
    ).subscribe((items) => this.addressSuggestions.set(items));
    this.load();
  }
  load(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');
    this.businessesService.businesses().pipe(finalize(() => this.isLoading.set(false))).subscribe({
      next: (businesses) => this.businesses.set(businesses),
      error: (error) => this.errorMessage.set(httpErrorMessage(error, 'Unable to load business requests.')),
    });
  }
  selectQueue(queue: BusinessQueue): void { this.selectedQueue.set(queue); }
  statusLabel(status?: string | null): string {
    return ({ Pending: 'Approval requested', CorrectionRequested: 'Correction requested', OnHold: 'On hold' } as Record<string, string>)[status || 'Pending'] || status || 'Approval requested';
  }
  openInternalForm(): void { this.internalForm = this.emptyInternalForm(); this.createError.set(''); this.createMessage.set(''); this.showInternalForm.set(true); }
  closeInternalForm(): void { if (!this.isCreating()) this.showInternalForm.set(false); }
  searchAddress(value: string): void {
    this.internalForm.addressLine1 = value; this.selectedAddress.set('');
    this.internalForm.latitude = null; this.internalForm.longitude = null;
    if (value.trim().length < 3) { this.addressSuggestions.set([]); return; }
    this.addressTerms.next(value.trim());
  }
  selectAddress(suggestion: LocationSuggestion): void {
    this.addressSuggestions.set([]); this.isAddressSearching.set(true); this.createError.set('');
    this.locations.placeDetails(suggestion.placeId).pipe(finalize(() => this.isAddressSearching.set(false))).subscribe({
      next: (location) => {
        this.internalForm.addressLine1 = location.formattedAddress;
        this.internalForm.city = location.city ?? '';
        this.internalForm.state = location.state ?? '';
        this.internalForm.country = location.country ?? 'India';
        this.internalForm.postalCode = (location.postalCode ?? location.zipCode ?? '').replace(/\D/g, '').slice(0, 6);
        this.internalForm.latitude = location.latitude; this.internalForm.longitude = location.longitude;
        this.selectedAddress.set(location.formattedAddress);
      },
      error: (error) => this.createError.set(httpErrorMessage(error, 'Unable to load the selected Google address.')),
    });
  }
  sanitizePhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const sanitized = input.value.replace(/\D/g, '').slice(0, 10);
    input.value = sanitized;
    this.internalForm.phone = sanitized;
  }
  sanitizePostalCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const sanitized = input.value.replace(/\D/g, '').slice(0, 6);
    input.value = sanitized;
    this.internalForm.postalCode = sanitized;
  }
  createInternalBusiness(): void {
    this.createError.set(''); this.createMessage.set('');
    const required = [this.internalForm.legalName, this.internalForm.ownerFirstName, this.internalForm.ownerEmail,
      this.internalForm.phone, this.internalForm.temporaryPassword, this.internalForm.locationName,
      this.internalForm.addressLine1, this.internalForm.city, this.internalForm.state, this.internalForm.postalCode];
    if (required.some((value) => !value.trim())) { this.createError.set('Complete all required company, owner and primary location fields.'); return; }
    if (!/^[6-9]\d{9}$/.test(this.internalForm.phone)) { this.createError.set('Enter a valid 10-digit Indian mobile number.'); return; }
    if (!/^[1-9]\d{5}$/.test(this.internalForm.postalCode)) { this.createError.set('Enter a valid 6-digit PIN code.'); return; }
    if (this.internalForm.latitude === null || this.internalForm.longitude === null) { this.createError.set('Select the primary location from the Google suggestions.'); return; }
    this.isCreating.set(true);
    this.businessesService.createInternalBusiness(this.internalForm).pipe(finalize(() => this.isCreating.set(false))).subscribe({
      next: () => { this.createMessage.set('ZepBazaar internal business is active and ready for staff access.'); this.load(); setTimeout(() => this.showInternalForm.set(false), 900); },
      error: (error) => this.createError.set(httpErrorMessage(error, 'Unable to create the internal business.')),
    });
  }
  private emptyInternalForm(): CreateInternalBusinessRequest {
    return { legalName: 'ZepBazaar', tradingName: 'ZepBazaar', registrationNumber: '', taxNumber: '',
      ownerFirstName: '', ownerLastName: '', ownerEmail: '', phone: '', temporaryPassword: '', website: '',
      addressLine1: '', addressLine2: '', city: '', state: '', country: 'India', postalCode: '', locationName: 'ZepBazaar HQ',
      latitude: null, longitude: null };
  }
}
